/**
 * `/albums/:albumId` — TASK 8.1 Step 3.
 *
 * The mock's `_album(V,id,q)` (L1039-1041) + template (L276-320), wired to
 * `GET /api/albums/:id` (TASK 4.1). This is the DOCUMENTED EXCEPTION to the
 * AC 3.9 browse exclusion: the endpoint returns `available:false` tracks so
 * they can be marked in place rather than silently renumbering the album
 * (dto.ts `albumTrackSchema`), which makes album-detail one of the four
 * "retain" surfaces of ../../components/AvailabilityBadge.js.
 *
 * ── WHAT THE MOCK GOT WRONG AND WHAT CHANGED ──────────────────────────────
 *   * the per-row badge cascade was inlined here (L1041) and a DIFFERENT one
 *     was inlined in `_card()` — both now call `availabilityState()`;
 *   * the meta line printed `al.year ? al.year+' · ' : ''`, which is correct
 *     but only by accident of JS truthiness; {@link albumMetaLine} makes the
 *     "no year ⇒ no segment" rule explicit and impossible to regress (AC 4.2);
 *   * the row list was unbounded — `tracksTruncated` plus a bounded render
 *     window replace it (AC 4.3).
 *
 * ── AC 5.6 CURRENT-TRACK INDICATOR ────────────────────────────────────────
 * The row for the track that is playing swaps its number for the mock's
 * three-bar equaliser and takes the `--sm-color-panel-active` background.
 * That has to survive a `setState` from the transport, so the view subscribes
 * to the store — and the subscription is GUARDED: the transport patches `pos`
 * on every `timeupdate` (~4×/s), so `syncFromStore` memoises the current
 * track id and the favourites arrays (by identity) and returns without
 * touching the DOM when none of them changed. When the current track really
 * moves, exactly the two affected rows repaint (the one that stopped being
 * current and the one that became it); everything else keeps its nodes, so
 * focus inside the list survives and the equaliser's CSS animation is never
 * restarted by a mere position tick.
 *
 * @module
 */

import {
  ERROR_CODES,
  TESTIDS,
  actionsForButton,
  artistRowLink,
  favoriteTrackButton,
  ordinalTestId,
  playTrackButton,
  unfavoriteTrackButton,
} from '@streamymusic/shared';

import {
  AVAILABILITY_COPY,
  AvailabilityBadge,
  availabilityOpacity,
  availabilityState,
  isPlayable,
} from '../../components/AvailabilityBadge.js';
import { VirtualList } from '../../components/VirtualList.js';
import {
  EmptyBlock,
  ErrorBlock,
  GoneBlock,
  LoadingBlock,
  NotFoundBlock,
} from '../../components/StateBlocks.js';
import { append, el } from '../../components/primitives/dom.js';
import {
  albumArtUrl,
  formatDuration,
  formatSeconds,
  gradientFor,
  initialsOf,
} from '../../components/primitives/format.js';
import { playsLabel } from '../stats/StatsView.js';

/**
 * Bounded render window for the track list. Albums are naturally small, but
 * "naturally" is not a bound: ui-contract §10.6's scale table caps ordered
 * lists at 200 rows with a "Show more (N more)" affordance, and the DTO
 * already carries `tracksTruncated` for the pathological case.
 */
export const TRACK_WINDOW = 200;

/**
 * Shown when the album was rendered from the LOCAL catalog cache because the
 * network was unreachable (AC 13.2). Says so plainly: a cached copy can lag the
 * server's, and a screen that silently pretended otherwise would be the kind of
 * quiet lie this codebase's error surfaces exist to avoid.
 */
export const OFFLINE_ALBUM_NOTICE =
  'You’re offline — showing this album from your downloads. ' +
  'Tracks without the ⌒ marker are on this device and will play.';

/** mock L286 — the 410 banner. */
export const ALBUM_GONE_MESSAGE =
  '⊘ This album is no longer available — it was removed from the source Drive folder. ' +
  "Retrying won't bring it back.";

/**
 * The meta line under the album title.
 *
 * AC 4.2 lives here: a missing `year` produces NO segment at all, not an empty
 * one and certainly not the string "null"/"undefined". ui-contract §10.6 pins
 * the expected output for the seeded null-year album: "…5 tracks · 18:55" —
 * the plays segment is appended AFTER that pinned prefix, so the captured
 * strings still lead the line.
 *
 * `playCount` is the header's user-scoped album total (dto.ts
 * `albumDetailResponseSchema`): the SQL sum over the whole album, so it stays
 * authoritative even when `tracksTruncated` capped the rows. Rendered through
 * the shared {@link playsLabel} ("1,234 plays" / "1 play") and ALWAYS present:
 * unlike a missing year, `0` is a real answer about this user's listening,
 * not an unknown.
 *
 * @param {{ year?: number, trackCount: number, totalDurationS: number, playCount: number }} album
 * @returns {string}
 */
export function albumMetaLine(album) {
  const parts = [];
  if (typeof album.year === 'number' && Number.isFinite(album.year)) parts.push(String(album.year));
  parts.push(`${album.trackCount} track${album.trackCount === 1 ? '' : 's'}`);
  parts.push(formatDuration(album.totalDurationS));
  parts.push(playsLabel(album.playCount));
  return parts.join(' · ');
}

/**
 * The compact per-row play-count indicator, or `null` for a never-played
 * track. "Nothing rather than a muted 0" follows this file's own meta-line
 * rule (no year ⇒ no segment): a row is dense already — number, title,
 * badges, duration, ♥, ⋯ — and stamping "▶ 0" on a whole library the user
 * has not played yet would be noise, not information. The count takes the
 * stats rail's thousands separator so the two surfaces can never disagree
 * about how a number reads.
 *
 * @param {{ playCount: number }} track
 * @returns {string|null}
 */
export function trackPlayCountLabel(track) {
  return track.playCount > 0 ? `▶ ${track.playCount.toLocaleString()}` : null;
}

/**
 * The number cell of a track row. Never "null" — `discNo`/`trackNo` are
 * `.nullable()` in the DTO because a loose file genuinely has no tag.
 *
 * @param {{ trackNo: number|null }} track
 * @returns {string}
 */
export function trackNumberLabel(track) {
  return typeof track.trackNo === 'number' ? String(track.trackNo) : '–';
}

/** mock L303 — the now-playing equaliser. */
function Equalizer() {
  const eq = el('span', { className: 'sm-eq', attrs: { 'aria-label': 'Now playing' } });
  append(
    eq,
    el('span', { className: 'sm-eq-bar' }),
    el('span', { className: 'sm-eq-bar' }),
    el('span', { className: 'sm-eq-bar' }),
  );
  return eq;
}

/**
 * @typedef {object} PlayRequest
 * @property {import('@streamymusic/shared').AlbumDetailResponse} album the
 *   context the tracks came from — the transport needs the artist/album names
 *   and the `context_id` the play-event contract requires for an album play
 * @property {import('@streamymusic/shared').AlbumTrack[]} tracks PLAYABLE only
 * @property {number} index where in `tracks` to start
 * @property {boolean} shuffle
 */

/**
 * @typedef {object} AlbumDetailHandlers
 * @property {(request: PlayRequest) => void} onPlay
 * @property {(album: import('@streamymusic/shared').AlbumDetailResponse) => void} onToggleFavoriteAlbum
 * @property {(track: import('@streamymusic/shared').AlbumTrack) => void} onToggleFavoriteTrack
 * @property {(album: import('@streamymusic/shared').AlbumDetailResponse) => void} onAlbumActions
 * @property {(track: { id: string, title: string, albumId?: string, artistId?: string, durationS?: number, format?: string, formatSupported?: boolean }, album?: import('@streamymusic/shared').AlbumDetailResponse) => void} onTrackActions
 *   the id and the title are all that is REQUIRED, so the FULL PLAYER can
 *   reuse it for the currently-playing item (which is a store `Track`, not an
 *   `AlbumTrack`). `album` is passed when the caller has it, and is what lets
 *   the sheet offer "Download track" — a single-track download needs the
 *   artist/album/duration metadata that lives on the album, not on the track
 *   (see modules/app/index.js#requestTrackDownload).
 * @property {(message: string, context: { state: AvailabilityState, trackId: string }) => void} onBlocked
 *   non-playable tap (§10.6 toast copy). `context` names WHICH item was refused and
 *   WHY — TASK 10.1's `offline_play_attempted` SLI counts refused offline taps
 *   (§11 `succeeded: false`) and cannot report a `track_id` it was never told.
 */

/**
 * Builds the `/albums/:id` section and starts loading it.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'getAlbum'>,
 *   heading: HTMLElement,
 *   albumId: string,
 *   handlers: AlbumDetailHandlers,
 *   getState: () => import('../../app/state-store.js').AppState,
 *   subscribe: (listener: (next: import('../../app/state-store.js').AppState) => void) => () => void,
 *   offline?: boolean,
 *   downloadedAlbums?: readonly string[],
 *   downloadedTracks?: readonly string[],
 *   offlineAlbum?: (albumId: string) => Promise<import('@streamymusic/shared').AlbumDetailResponse|null>,
 *   onAlbumLoaded?: (album: import('@streamymusic/shared').AlbumDetailResponse) => void,
 *   canGoBack?: () => boolean,
 *   onBack?: () => void,
 *   announce?: (text: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 *     the app's diagnostics seam (modules/app/index.js). A SERVER contract
 *     violation detected while deciding a track's availability marker is
 *     reported here rather than only to the user's devtools console.
 * }} options
 *   `announce` is the shell's persistent polite live region (LiveRegions.js)
 *   — see StateBlocks.js#ErrorBlock for why the blocks cannot announce
 *   themselves.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function AlbumDetail(options) {
  const {
    api,
    heading,
    albumId,
    handlers,
    getState,
    subscribe,
    offline = false,
    downloadedAlbums = [],
    downloadedTracks = [],
    offlineAlbum,
    onAlbumLoaded,
    // #back — the back arrow's two halves. Defaults keep this view standalone:
    // no in-app history means the arrow stays the plain artist link it was.
    canGoBack = () => false,
    onBack = () => {},
    announce,
    warn = (message) => {
      console.warn(message);
    },
  } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Album detail' } });
  const backLink = el('p', { className: 'sm-backlink sm-backlink--10' });
  const header = el('div', { className: 'sm-media-header' });
  const trackHost = el('div');
  const notice = el('div');
  append(section, backLink, header, trackHost, notice);

  const albumIsDownloaded = new Set(downloadedAlbums).has(albumId);
  /**
   * PER-TRACK download truth (Dev/Product W1).
   *
   * `downloadedAlbums` is a whole-album roll-up — `downloadedAlbumIds()` admits
   * an album only when EVERY one of its records is cached — and flattening it
   * to one boolean for every row in the album was wrong in both directions.
   * One failed track out of ten made the nine cached tracks render "⌒ not
   * downloaded" and refuse to play, and it corrupted the §11 Offline-success
   * SLI by reporting `offline_play_attempted{succeeded:false}` for tracks that
   * would have played fine. Now each row asks about ITSELF.
   *
   * The album roll-up is still what the album-level affordances use, because
   * "can I press PLAY on this whole album offline" is genuinely an all-or-
   * nothing question.
   */
  const downloadedTrackIds = new Set(downloadedTracks);

  let disposed = false;
  let generation = 0;
  /**
   * Mounted rows, keyed by track id, with the three nodes AC 5.6 repaints.
   * Held explicitly rather than re-queried so the repaint is O(rows) with no
   * selector work and no dependence on class names staying put.
   *
   * @type {Map<string, { row: HTMLElement, numberCell: HTMLElement, titleLine: HTMLElement, favoriteButton: HTMLElement, title: string, label: string, favorited: boolean }>}
   */
  const rowsByTrackId = new Map();
  /** The album-level ♥, held so an optimistic toggle (and its rollback) shows. */
  /** @type {HTMLElement|null} */
  let albumFavoriteButton = null;
  /** The loaded payload — handlers need the album, not just its tracks. */
  /** @type {import('@streamymusic/shared').AlbumDetailResponse|null} */
  let loaded = null;

  /* ── header ─────────────────────────────────────────────────────────── */

  /**
   * @param {import('@streamymusic/shared').AlbumDetailResponse} data
   * @returns {void}
   */
  function paintHeader(data) {
    heading.textContent = data.title;
    const artistHref = `#/artists/${encodeURIComponent(data.artist.id)}`;

    /*
     * A BACK ARROW MUST GO BACK (#back).
     *
     * This link used to be hardcoded to the album's ARTIST, which is only the
     * right destination when you arrived from the artist. Reached from a genre
     * (browse → genres → genre → album) it threw the user onto the artist page
     * at scroll top — skipping the genre they were browsing and losing its
     * position. Same for an album opened from search, favorites, downloads, home
     * or the player's "Go to album".
     *
     * So when there IS somewhere to go back to, this performs a real history
     * back: the router re-renders the previous route and the route announcer
     * restores its scroll and list depth. The artist stays one tap away — it is
     * the album title's own subtitle link below, and the ⋯ sheet's "Go to
     * artist".
     *
     * The `href` REMAINS the artist either way, deliberately: it is the correct
     * degradation for a cold start / deep link (nothing to go back to), it keeps
     * the control a real link for middle-click and long-press, and it keeps this
     * element's testid stable so the header's ordinal-2 artist link is unaffected.
     */
    const canBack = canGoBack();
    const back = el('a', {
      className: 'sm-backlink__a',
      text: canBack ? '← Back' : `← ${data.artist.name}`,
      testId: artistRowLink(data.artist.id),
      attrs: { href: artistHref },
      ...(canBack
        ? {
            on: {
              click: (event) => {
                event.preventDefault();
                onBack();
              },
            },
          }
        : {}),
    });
    backLink.replaceChildren(back);

    const cover = el('div', {
      className: 'sm-cover sm-cover--hero',
      styleProps: { background: gradientFor(data.id) },
    });
    append(
      cover,
      el('span', {
        className: 'sm-cover__initials',
        text: initialsOf(data.title),
        // Decorative duplication of the heading — same treatment as the grid
        // cards (primitives/Card.js) and the ArtistsList thumbs.
        attrs: { 'aria-hidden': 'true' },
      }),
    );

    // Real cover art over the gradient — the SAME rule and img layer the grid
    // cards use (primitives/Card.js), just built inline because the hero is not
    // a Card. Only for a `covers/…` album; on a load error it removes itself so
    // the gradient + initials fallback shows through.
    const heroArtUrl = albumArtUrl(data);
    if (heroArtUrl !== undefined) {
      const art = el('img', {
        className: 'sm-cover__art-img',
        attrs: { src: heroArtUrl, alt: '', loading: 'lazy', decoding: 'async' },
        on: { error: () => art.remove() },
      });
      append(cover, art);
    }

    const body = el('div', { className: 'sm-media-header__body' });
    append(
      body,
      heading,
      append(
        el('p', { className: 'sm-media-header__artist' }),
        el('a', {
          text: data.artist.name,
          // Second instance of the same derived id on this page — the
          // contract's ordinal-duplicate convention (testids.ts).
          testId: ordinalTestId(artistRowLink(data.artist.id), 2),
          attrs: { href: artistHref },
        }),
      ),
      el('p', { className: 'sm-meta', text: albumMetaLine(data) }),
    );

    if (!data.available) {
      append(
        body,
        el('div', {
          className: 'sm-banner sm-banner--mb',
          text: ALBUM_GONE_MESSAGE,
          attrs: { role: 'status' },
        }),
      );
    } else {
      append(body, ActionRow(data));
    }

    header.replaceChildren(cover, body);
  }

  /**
   * mock L289-294 — PLAY / SHUFFLE / ♥ / ⋯. Hidden entirely for a gone album,
   * exactly as the mock does (`alb.playable`): there is nothing to play.
   *
   * @param {import('@streamymusic/shared').AlbumDetailResponse} data
   * @returns {HTMLElement}
   */
  function ActionRow(data) {
    const playable = data.tracks.filter((track) => isPlayable(stateOf(track)));
    const favorited = getState().catalog.favorites.albums.includes(data.id);

    const favoriteButton = el('button', {
      className: 'sm-btn sm-btn--panel-icon',
      text: '♥',
      testId: TESTIDS.TOGGLE_FAVORITE_ALBUM_BUTTON,
      attrs: {
        type: 'button',
        'aria-label': 'Toggle favorite album',
        'aria-pressed': String(favorited),
      },
      on: { click: () => handlers.onToggleFavoriteAlbum(data) },
    });
    albumFavoriteButton = favoriteButton;
    paintFavorite(favoriteButton, favorited, 'var(--sm-color-text-muted)');

    const row = el('div', { className: 'sm-actions' });
    append(
      row,
      el('button', {
        className: 'sm-btn sm-btn--primary sm-btn--display',
        text: '▶ PLAY',
        testId: TESTIDS.PLAY_BUTTON,
        attrs: { type: 'button' },
        on: {
          click: () => handlers.onPlay({ album: data, tracks: playable, index: 0, shuffle: false }),
        },
      }),
      el('button', {
        className: 'sm-btn sm-btn--accent sm-btn--display',
        text: '⤨ SHUFFLE',
        testId: TESTIDS.SHUFFLE_BUTTON,
        attrs: { type: 'button' },
        on: {
          click: () => handlers.onPlay({ album: data, tracks: playable, index: 0, shuffle: true }),
        },
      }),
      favoriteButton,
      el('button', {
        className: 'sm-btn sm-btn--panel-icon',
        text: '⋯',
        testId: TESTIDS.ALBUM_ACTIONS_BUTTON,
        attrs: { type: 'button', 'aria-label': 'Album actions' },
        on: { click: () => handlers.onAlbumActions(data) },
      }),
    );
    return row;
  }

  /* ── track rows ─────────────────────────────────────────────────────── */

  /**
   * @param {import('@streamymusic/shared').AlbumTrack} track
   * @returns {import('../../components/AvailabilityBadge.js').AvailabilityState|null}
   */
  function stateOf(track) {
    return availabilityState({
      available: track.available,
      formatSupported: track.formatSupported,
      offline,
      // PER-TRACK (W1). A track is playable offline iff ITS OWN body is
      // cached — falling back to the album roll-up only for a row we were
      // told nothing about, which is the pre-W1 behaviour and still the safe
      // default (it marks, rather than promises).
      downloaded:
        downloadedTrackIds.size === 0 ? albumIsDownloaded : downloadedTrackIds.has(track.id),
      kind: 'track',
      // A RETAIN surface: album-detail keeps the reference and marks it.
      surface: 'album',
      // A server CONTRACT VIOLATION, not a UI condition — it goes to the app's
      // diagnostics seam, not only to the user's devtools console.
      onScopeViolation: (message) => warn(message, { surface: 'album', albumId }),
    });
  }

  /**
   * @param {import('@streamymusic/shared').AlbumTrack} track
   * @param {import('@streamymusic/shared').AlbumTrack[]} playable
   * @param {import('@streamymusic/shared').AlbumDetailResponse} album
   * @returns {HTMLElement}
   */
  function TrackRow(track, playable, album) {
    const marker = stateOf(track);
    const favorited = getState().catalog.favorites.tracks.includes(track.id);

    const row = el('div', {
      className: 'sm-track-row',
      styleProps: { opacity: String(availabilityOpacity(marker)) },
    });

    const numberCell = el('span', { className: 'sm-track-row__no' });
    const titleLine = el('span', { className: 'sm-line-primary', text: track.title });

    const play = el('button', {
      className: 'sm-track-row__play',
      testId: playTrackButton(track.title),
      attrs: { type: 'button', 'aria-label': `Play ${track.title}` },
      on: {
        click: () => {
          if (marker !== null) {
            // Never start a stream that cannot work — the §10.6 per-state copy
            // instead (the mock's three `_toast(...)` branches, L1041).
            handlers.onBlocked(AVAILABILITY_COPY[marker].banner, {
              state: marker,
              trackId: track.id,
            });
            return;
          }
          handlers.onPlay({
            album,
            tracks: playable,
            index: playable.indexOf(track),
            shuffle: false,
          });
        },
      },
    });

    const textCell = el('span', { className: 'sm-flex-fill' });
    append(textCell, titleLine, marker === null ? null : AvailabilityBadge({ state: marker }));

    // AC 12.1 — the caller's all-time qualifying count for THIS track, in the
    // stats rail's yellow treatment. A never-played track shows nothing (see
    // trackPlayCountLabel); the duration cell keeps the trailing position.
    const playCount = trackPlayCountLabel(track);

    append(
      play,
      numberCell,
      textCell,
      playCount === null ? null : el('span', { className: 'sm-plays', text: playCount }),
      el('span', { className: 'sm-line-trailing', text: formatSeconds(track.durationS) }),
    );

    const favoriteButton = el('button', {
      className: 'sm-btn sm-btn--icon',
      text: '♥',
      testId: favorited ? unfavoriteTrackButton(track.title) : favoriteTrackButton(track.title),
      attrs: {
        type: 'button',
        'aria-label': `${favorited ? 'Unfavorite' : 'Favorite'} ${track.title}`,
        'aria-pressed': String(favorited),
      },
      on: { click: () => handlers.onToggleFavoriteTrack(track) },
    });
    paintFavorite(favoriteButton, favorited, 'var(--sm-color-text-dim-2)');

    append(
      row,
      play,
      favoriteButton,
      el('button', {
        className: 'sm-btn sm-btn--icon sm-btn--icon-muted',
        text: '⋯',
        testId: actionsForButton(track.title),
        attrs: { type: 'button', 'aria-label': `Actions for ${track.title}` },
        // The ALBUM travels with the track: a download needs the
        // artist/album/duration the offline catalog stores for airplane-mode
        // playback, and an `AlbumTrack` carries none of them (W2).
        on: { click: () => handlers.onTrackActions(track, album) },
      }),
    );

    const entry = {
      row,
      numberCell,
      titleLine,
      favoriteButton,
      title: track.title,
      label: trackNumberLabel(track),
      favorited,
    };
    rowsByTrackId.set(track.id, entry);
    paintCurrent(track.id, entry);
    return row;
  }

  /**
   * The shared ♥ treatment: `aria-pressed` carries the STATE (so it is never
   * colour-only, §8.5) and the tint follows it. Called on build and again on
   * every store change, which is what makes an optimistic favorite toggle —
   * and its rollback — visible without rebuilding the row.
   *
   * @param {HTMLElement} button
   * @param {boolean} favorited
   * @param {string} restingColor
   */
  function paintFavorite(button, favorited, restingColor) {
    button.setAttribute('aria-pressed', String(favorited));
    button.style.setProperty('color', favorited ? 'var(--sm-color-pink)' : restingColor);
  }

  /**
   * AC 5.6 — paints ONE row's current-ness. Called when the row is built and
   * again (by {@link syncFromStore}) for exactly the rows whose current-ness
   * flipped — a targeted repaint, so focus inside the list survives and the
   * equaliser is not rebuilt (its CSS animation would restart from frame 0).
   *
   * @param {string} trackId
   * @param {{ row: HTMLElement, numberCell: HTMLElement, titleLine: HTMLElement, label: string }} entry
   */
  function paintCurrent(trackId, entry) {
    const current = getState().playback.current;
    const isCurrent = current !== null && current.id === trackId;

    entry.row.style.setProperty(
      'background',
      isCurrent ? 'var(--sm-color-panel-active)' : 'var(--sm-color-panel)',
    );
    entry.titleLine.style.setProperty(
      'color',
      isCurrent ? 'var(--sm-color-teal)' : 'var(--sm-color-text)',
    );
    entry.numberCell.replaceChildren(isCurrent ? Equalizer() : el('span', { text: entry.label }));
  }

  /*
   * What the last repaint was painted FROM, held by identity. The transport
   * writes `{pos}` ~4×/s while playing; none of those ticks change these
   * three, so `syncFromStore` costs a few comparisons and no DOM work.
   */
  /** @type {string|null} */
  let paintedCurrentId = null;
  /** @type {readonly string[]} */
  let paintedFavTracks = getState().catalog.favorites.tracks;
  /** @type {readonly string[]} */
  let paintedFavAlbums = getState().catalog.favorites.albums;

  /**
   * Repaints what the store owns — current track + favourite states — but
   * ONLY the slices that actually changed, and within those, only the rows
   * whose own state flipped (C9).
   */
  function syncFromStore() {
    const state = getState();
    const current = state.playback.current;
    const currentId = current === null ? null : current.id;
    const favorites = state.catalog.favorites;

    if (currentId !== paintedCurrentId) {
      const wasId = paintedCurrentId;
      paintedCurrentId = currentId;
      // At most TWO rows repaint: the one that stopped being current and the
      // one that became it. Rows for tracks outside this album simply miss.
      for (const id of [wasId, currentId]) {
        if (id === null) continue;
        const entry = rowsByTrackId.get(id);
        if (entry !== undefined && entry.row.isConnected) paintCurrent(id, entry);
      }
    }

    if (favorites.tracks !== paintedFavTracks) {
      paintedFavTracks = favorites.tracks;
      // The Set is hoisted OUT of the row loop: one build, O(1) lookups.
      const favoritedTracks = new Set(favorites.tracks);
      for (const [trackId, entry] of rowsByTrackId) {
        const favorited = favoritedTracks.has(trackId);
        if (favorited === entry.favorited || !entry.row.isConnected) continue;
        entry.favorited = favorited;
        paintFavorite(entry.favoriteButton, favorited, 'var(--sm-color-text-dim-2)');
        entry.favoriteButton.dataset.testid = favorited
          ? unfavoriteTrackButton(entry.title)
          : favoriteTrackButton(entry.title);
        entry.favoriteButton.setAttribute(
          'aria-label',
          `${favorited ? 'Unfavorite' : 'Favorite'} ${entry.title}`,
        );
      }
    }

    if (favorites.albums !== paintedFavAlbums) {
      paintedFavAlbums = favorites.albums;
      if (albumFavoriteButton !== null && albumFavoriteButton.isConnected && loaded !== null) {
        paintFavorite(
          albumFavoriteButton,
          favorites.albums.includes(loaded.id),
          'var(--sm-color-text-muted)',
        );
      }
    }
  }

  const unsubscribe = subscribe(() => {
    if (disposed) return;
    syncFromStore();
  });

  /* ── render ─────────────────────────────────────────────────────────── */

  /**
   * Paints a loaded album. Extracted from {@link load} so the ONLINE path and
   * the AC 13.2 OFFLINE path render the identical screen — same rows, same
   * availability markers, same `handlers.onPlay`. A reduced "offline variant"
   * would be a second album screen to keep in sync, and the whole point of
   * AC 13.2 is that a downloaded album behaves like an album.
   *
   * @param {import('@streamymusic/shared').AlbumDetailResponse} data
   * @returns {void}
   */
  function renderAlbum(data) {
    paintHeader(data);

    if (data.tracks.length === 0) {
      // An indexed album whose tracks all vanished, or one still mid-sync.
      // Never a blank panel: §10.6 requires every surface to say something.
      trackHost.replaceChildren(
        EmptyBlock({
          headline: 'NO TRACKS ON THIS ONE',
          message: 'This album has no playable tracks yet.',
          ...(announce === undefined ? {} : { announce }),
        }),
      );
      return;
    }

    const playable = data.tracks.filter((track) => isPlayable(stateOf(track)));
    rowsByTrackId.clear();
    const current = getState().playback.current;
    paintedCurrentId = current === null ? null : current.id;
    paintedFavTracks = getState().catalog.favorites.tracks;
    paintedFavAlbums = getState().catalog.favorites.albums;
    const list = VirtualList({
      items: [...data.tracks],
      windowSize: TRACK_WINDOW,
      renderItem: (track) => TrackRow(track, playable, data),
      // The track list is an ORDERED list, so it takes ui-contract §10.6's
      // ordered-list affordance "Show more (N more)" (the queue's variant),
      // not the grids' "Load more … ↓".
      loadMoreLabel: ({ remaining }) => `Show more (${remaining} more)`,
      listClassName: ['sm-stack-6', 'sm-stack-6--top'],
    });
    trackHost.replaceChildren(list.element);

    if (data.tracksTruncated) {
      // Never a silent truncation: say so rather than letting the row count
      // quietly disagree with `trackCount`.
      notice.replaceChildren(
        el('p', {
          className: 'sm-hint',
          text: `Showing the first ${data.tracks.length} of ${data.trackCount} tracks.`,
          attrs: { role: 'status' },
        }),
      );
    }
  }

  /**
   * The AC 13.2 offline render: {@link renderAlbum} plus an honest banner
   * saying WHERE this came from, because a locally-cached catalog copy can be
   * older than the server's and the user is entitled to know that.
   *
   * @param {import('@streamymusic/shared').AlbumDetailResponse} data
   * @returns {void}
   */
  function paintOffline(data) {
    renderAlbum(data);
    notice.replaceChildren(
      el('p', {
        className: 'sm-hint',
        text: OFFLINE_ALBUM_NOTICE,
        // `status`, not `alert`: this is context for a screen that rendered
        // successfully, not an interruption.
        attrs: { role: 'status' },
      }),
    );
    announce?.(OFFLINE_ALBUM_NOTICE);
  }

  /* ── load ───────────────────────────────────────────────────────────── */

  async function load() {
    const own = (generation += 1);
    notice.replaceChildren();
    header.replaceChildren(heading);
    trackHost.replaceChildren(LoadingBlock({ count: 6, label: 'Loading album' }));

    try {
      const data = await api.getAlbum(albumId);
      if (disposed || own !== generation) return;
      loaded = data;
      /*
       * AC 13.2 — the ONLY moment this album's full payload is in hand while
       * the network works. The composition root persists it to the offline
       * catalog so `offlineAlbum` can render this same screen with no network
       * later. Announced through a callback rather than done here because the
       * view owns markup, not storage.
       */
      onAlbumLoaded?.(data);
      renderAlbum(data);
    } catch (error) {
      if (disposed || own !== generation) return;
      // Branch on the MACHINE-READABLE code (api-client.ts `ApiError.code`,
      // ERROR_CODES), not the transport status: the status is how the code
      // travelled, the code is what it means.
      const code = /** @type {{ code?: string }} */ (error).code;

      /*
       * AC 13.2 — THE AIRPLANE-MODE PATH (QA/Product C3).
       *
       * This is what makes "downloaded content plays in airplane mode with
       * title/artist/album + cover art" reachable from the UI at all. Before
       * it, `getAlbum()` was called unconditionally and a cold offline start
       * landed in the generic ErrorBlock with a Retry that could never
       * succeed — the feature's headline capability had infrastructure behind
       * it (`putCatalog`/`getCatalog`, the per-track catalog metadata
       * `enqueue` persists) and no way in.
       *
       * ONLY on a transport-shaped failure, and only offline: a 404/410 is a
       * real answer from a reachable server and must keep its own screen, and
       * an ONLINE fetch failure is a fault the user should see rather than
       * have papered over with a possibly-stale local copy.
       *
       * The rendered screen is the ORDINARY one — same rows, same badges, same
       * `handlers.onPlay`, cover art through the service worker's `/api/art/*`
       * interception (never a `blob:` URL, so CSP stays `'self'`). Tracks whose
       * bytes are not cached keep their "⌒ not downloaded" marker and stay
       * unplayable, which is AC 13.4 holding on the very screen that finally
       * satisfies AC 13.2.
       */
      const transportShaped = code === undefined || code === ERROR_CODES.INTERNAL;
      if (offline && transportShaped && offlineAlbum !== undefined) {
        try {
          const cachedAlbum = await offlineAlbum(albumId);
          if (disposed || own !== generation) return;
          if (cachedAlbum !== null) {
            loaded = cachedAlbum;
            paintOffline(cachedAlbum);
            return;
          }
        } catch {
          /* the local copy is an enhancement; fall through to the error UI */
        }
      }

      header.replaceChildren(heading);
      if (code === ERROR_CODES.NOT_FOUND) {
        heading.textContent = 'Album';
        trackHost.replaceChildren(
          NotFoundBlock({
            message: 'That album isn’t here — it may have been removed, or never existed.',
          }),
        );
        return;
      }
      if (code === ERROR_CODES.GONE) {
        // §10.6: 410 is permanent — "retry never succeeds", so no Retry. The
        // copy is the same removed-from-Drive truth the in-page banner tells.
        heading.textContent = 'Album';
        trackHost.replaceChildren(GoneBlock({ message: ALBUM_GONE_MESSAGE }));
        return;
      }
      trackHost.replaceChildren(
        ErrorBlock({
          onRetry: () => {
            void load();
          },
          ...(announce === undefined ? {} : { announce }),
        }),
      );
    }
  }

  void load();

  return {
    element: section,
    dispose() {
      disposed = true;
      unsubscribe();
    },
  };
}
